import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';
import { AppState, type NativeEventSubscription } from 'react-native';
import EventSource, { type EventSourceListener } from 'react-native-sse';

export function useSSE<T>(
  url: string,
  callback: (event: T) => boolean,
  errorCallback?: (error: Error) => void
) {
  const { authState } = useAuth();

  useEffect(() => {
    let es: EventSource;
    let appStateSubscription: NativeEventSubscription;
    if (!authState.loading && authState.token) {
      es = new EventSource(url, {
        headers: {
          Authorization: {
            toString: () => `Bearer ${authState.token}`
          }
        }
      });

      appStateSubscription = AppState.addEventListener(
        'change',
        (nextAppState) => {
          if (nextAppState === 'active') {
            // App became active, reconnect SSE
            es.open();
          } else if (
            nextAppState === 'background' ||
            nextAppState === 'inactive'
          ) {
            // App went to background, close SSE connection
            es.close();
          }
        }
      );

      const listener: EventSourceListener = (event) => {
        if (event.type === 'open') {
          // console.log('Open SSE connection.');
        } else if (event.type === 'message') {
          if (event.data === '[DONE]') {
            es.close();
            return;
          }
          const data = event.data ? JSON.parse(event.data) : null;
          const result = callback(data as T);
          if (result === false) {
            es.close();
          }
        } else if (event.type === 'error') {
          console.error('Connection error:', event.message);
          errorCallback?.(new Error(event.message));
        } else if (event.type === 'exception') {
          console.error('Error:', event.message, event.error);
          errorCallback?.(event.error);
        } else if (event.type === 'timeout') {
          console.warn('Connection timeout:', event);
        }
      };

      es.addEventListener('open', listener);
      es.addEventListener('message', listener);
      es.addEventListener('error', listener);
    }

    return () => {
      es?.removeAllEventListeners();
      es?.close();
      appStateSubscription?.remove();
    };
  }, [authState.loading, authState.token]);
}
